import { useState, useEffect } from "react";
import { api, countryFlag, errMsg } from "@/lib/api";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Loader2, Boxes } from "lucide-react";
import { toast } from "sonner";

const PRODUCTS = [
  { id: "residential", label: "Residential · gate:10000", duration: true },
  { id: "datacenter", label: "Datacenter · dc:10001", duration: false },
  { id: "isp", label: "ISP / Static · isp:10000", duration: false },
];

const COUNTRIES = [
  { code: "global", name: "Global / Random" },
  { code: "us", name: "United States" },
  { code: "gb", name: "United Kingdom" },
  { code: "ca", name: "Canada" },
  { code: "de", name: "Germany" },
  { code: "fr", name: "France" },
  { code: "nl", name: "Netherlands" },
  { code: "es", name: "Spain" },
  { code: "it", name: "Italy" },
  { code: "id", name: "Indonesia" },
  { code: "sg", name: "Singapore" },
  { code: "jp", name: "Japan" },
  { code: "au", name: "Australia" },
  { code: "br", name: "Brazil" },
  { code: "in", name: "India" },
];

const trigger = "rounded-none bg-black border-line font-mono text-sm";

function Field({ label, children }) {
  return (
    <div className="flex flex-col gap-1.5">
      <span className="overline">{label}</span>
      {children}
    </div>
  );
}

export default function DecodoDialog({ open, onOpenChange, onGenerated }) {
  const [configured, setConfigured] = useState(true);
  const [product, setProduct] = useState("residential");
  const [country, setCountry] = useState("global");
  const [count, setCount] = useState("10");
  const [sticky, setSticky] = useState(true);
  const [duration, setDuration] = useState("10");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    api.get("/settings").then(({ data }) => setConfigured(!!data.decodo_configured)).catch(() => {});
  }, [open]);

  const prod = PRODUCTS.find((p) => p.id === product) || PRODUCTS[0];

  const generate = async () => {
    setBusy(true);
    try {
      const body = {
        product,
        country: country === "global" ? null : country,
        count: parseInt(count, 10),
        sticky,
        duration: parseInt(duration, 10) || 10,
      };
      const { data } = await api.post("/pool/decodo", body);
      toast.success(`Decodo selesai · ${data.alive} IP hidup, ${data.new} baru`);
      onGenerated && onGenerated(data);
      onOpenChange(false);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !busy && onOpenChange(o)}>
      <DialogContent className="bg-panel border-line text-foreground max-w-lg" data-testid="decodo-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading tracking-tight flex items-center gap-2">
            <Boxes size={18} className="text-neon" /> Generate Decodo · SOCKS5
          </DialogTitle>
          <DialogDescription className="text-dim text-xs">
            Creates pool entries from Decodo. Each sticky entry = one stable exit IP, probed live and ready for Port Binding / Order / Node.
          </DialogDescription>
        </DialogHeader>

        {!configured && (
          <div className="rounded-md border border-signal/50 bg-signal/10 text-signal font-mono text-xs px-3 py-2" data-testid="decodo-not-configured">
            Decodo credentials not set. Add your Decodo username & password in Settings first.
          </div>
        )}

        <div className="grid grid-cols-2 gap-3">
          <Field label="Product">
            <Select value={product} onValueChange={setProduct}>
              <SelectTrigger className={trigger} data-testid="decodo-product"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-panel border-line">
                {PRODUCTS.map((p) => <SelectItem key={p.id} value={p.id} className="font-mono text-xs">{p.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </Field>
          <Field label="Country">
            <Select value={country} onValueChange={setCountry}>
              <SelectTrigger className={trigger} data-testid="decodo-country"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-panel border-line max-h-72">
                {COUNTRIES.map((c) => (
                  <SelectItem key={c.code} value={c.code} className="font-mono text-xs">
                    {c.code === "global" ? "🌐" : countryFlag(c.code.toUpperCase())} {c.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
          <Field label="Count">
            <Select value={count} onValueChange={setCount}>
              <SelectTrigger className={trigger} data-testid="decodo-count"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-panel border-line">
                {["5", "10", "20", "50"].map((n) => <SelectItem key={n} value={n} className="font-mono text-xs">{n} IP</SelectItem>)}
              </SelectContent>
            </Select>
          </Field>
          {prod.duration && sticky && (
            <Field label="Session duration (min)">
              <Select value={duration} onValueChange={setDuration}>
                <SelectTrigger className={trigger} data-testid="decodo-duration"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-panel border-line">
                  {["1", "5", "10", "30"].map((n) => <SelectItem key={n} value={n} className="font-mono text-xs">{n} min</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
          )}
        </div>

        <div className="flex items-center justify-between rounded-md border border-line bg-black px-3 py-2.5">
          <div>
            <span className="overline block">Sticky session</span>
            <span className="font-mono text-[11px] text-dim">{sticky ? "each entry = stable IP" : "rotating · IP changes per request"}</span>
          </div>
          <Switch checked={sticky} onCheckedChange={setSticky} data-testid="decodo-sticky" />
        </div>

        <div className="flex justify-end pt-1">
          <Button
            onClick={generate}
            disabled={busy || !configured}
            className="rounded-none bg-neon text-white hover:bg-neon/85 font-medium"
            data-testid="decodo-generate-btn"
          >
            {busy ? <Loader2 className="animate-spin" size={15} /> : <Boxes size={15} />}
            {busy ? "Generating..." : "Generate"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
